import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '@/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Eye, EyeOff, Loader2, ChevronRight, LogIn, Mail, Lock, User, Shield, ArrowLeft, KeyRound, X, Sparkles } from 'lucide-react';
import axios from 'axios';

const SAVED_USER_KEY = 'nexuzap_saved_user';
const API_URL = process.env.REACT_APP_BACKEND_URL;

function GlitchText({ text, className }) {
  return (
    <span className={`relative inline-block ${className}`}>
      <span className="relative z-10">{text}</span>
      <span className="absolute top-0 left-0.5 text-cyan-500/30 z-0 animate-glitch-1" aria-hidden="true">{text}</span>
      <span className="absolute top-0 -left-0.5 text-primary/30 z-0 animate-glitch-2" aria-hidden="true">{text}</span>
    </span>
  );
}

function ParticleBg() {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {Array.from({ length: 30 }).map((_, i) => (
        <div key={i} className="absolute rounded-full bg-primary/20" style={{
          width: `${Math.random() * 4 + 1}px`, height: `${Math.random() * 4 + 1}px`,
          left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`,
          animation: `float-particle ${Math.random() * 10 + 8}s ease-in-out infinite`,
          animationDelay: `${Math.random() * 5}s`
        }} />
      ))}
      <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-cyan-500/5 rounded-full blur-[120px]" />
    </div>
  );
}

function TypewriterText({ text, speed = 25 }) {
  const [displayed, setDisplayed] = useState('');
  const idx = useRef(0);
  useEffect(() => {
    setDisplayed('');
    idx.current = 0;
    const timer = setInterval(() => {
      if (idx.current < text.length) {
        setDisplayed(text.slice(0, idx.current + 1));
        idx.current++;
      } else clearInterval(timer);
    }, speed);
    return () => clearInterval(timer);
  }, [text]);
  return <>{displayed}<span className="animate-blink">|</span></>;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  // Steps: 'user' | 'password' | 'verification' | 'set_email' | 'verify_new_email'
  const [step, setStep] = useState('user');
  const [animDir, setAnimDir] = useState('next');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savedUser, setSavedUser] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [userId, setUserId] = useState(null);
  const [emailHint, setEmailHint] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [inputReady, setInputReady] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem(SAVED_USER_KEY);
    if (saved) {
      setSavedUser(saved);
      setUsername(saved);
      setStep('password');
    }
  }, []);

  useEffect(() => {
    if (step === 'user' || step === 'password') {
      setInputReady(false);
      const t = setTimeout(() => { setInputReady(true); inputRef.current?.focus(); }, 400);
      return () => clearTimeout(t);
    }
  }, [step]);

  const goToPassword = () => {
    if (!username.trim()) { toast.error('Digite seu usuario ou email'); return; }
    setAnimDir('next');
    setStep('password');
  };

  const goBackToUser = () => {
    setAnimDir('prev');
    setPassword('');
    setStep('user');
  };

  const clearSavedUser = () => {
    localStorage.removeItem(SAVED_USER_KEY);
    setSavedUser(null);
    setUsername('');
    setPassword('');
    setAnimDir('prev');
    setStep('user');
  };

  const handleLogin = async () => {
    if (!password) { toast.error('Digite sua senha'); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        username: username.trim(),
        password
      });
      const data = response.data;

      if (data.requires_email) {
        setUserId(data.user_id);
        setAnimDir('next');
        setStep('set_email');
        return;
      }
      if (data.requires_verification) {
        setUserId(data.user_id);
        setEmailHint(data.email_hint);
        setAnimDir('next');
        setStep('verification');
        toast.success('Codigo enviado para seu email!');
        return;
      }
      if (data.token) {
        localStorage.setItem('nexus-token', data.token);
        localStorage.setItem(SAVED_USER_KEY, username.trim());
        if (data.expired) { navigate('/blocked', { state: { reason: 'expired' } }); return; }
        toast.success('Login realizado!');
        window.location.href = '/dashboard';
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'blocked' || detail === 'expired') {
        navigate('/blocked', { state: { reason: detail } });
      } else if (detail === 'email_not_verified') {
        toast.error('Email nao verificado. Verifique sua caixa de entrada.');
      } else {
        toast.error(detail || 'Erro ao fazer login');
      }
    } finally { setLoading(false); }
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) { toast.error('Digite o codigo de 6 digitos'); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-code`, { user_id: userId, code: verificationCode });
      if (response.data.token) {
        localStorage.setItem('nexus-token', response.data.token);
        localStorage.setItem(SAVED_USER_KEY, username.trim());
        if (response.data.expired) { navigate('/blocked', { state: { reason: 'expired' } }); return; }
        toast.success('Login realizado!');
        window.location.href = '/dashboard';
      }
    } catch (error) { toast.error(error.response?.data?.detail || 'Codigo invalido'); }
    finally { setLoading(false); }
  };

  const handleSetEmail = async () => {
    if (!newEmail || !newEmail.includes('@')) { toast.error('Digite um email valido'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/set-email?user_id=${userId}`, { email: newEmail });
      setAnimDir('next');
      setStep('verify_new_email');
      toast.success('Codigo enviado!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao enviar'); }
    finally { setLoading(false); }
  };

  const handleVerifyNewEmail = async () => {
    if (verificationCode.length !== 6) { toast.error('Digite o codigo de 6 digitos'); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-email?user_id=${userId}`, { email: newEmail, code: verificationCode });
      if (response.data.token) {
        localStorage.setItem('nexus-token', response.data.token);
        localStorage.setItem(SAVED_USER_KEY, username.trim());
        toast.success('Email verificado!');
        window.location.href = '/dashboard';
      }
    } catch (error) { toast.error(error.response?.data?.detail || 'Codigo invalido'); }
    finally { setLoading(false); }
  };

  const resendCode = async () => {
    setResending(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/resend-code`, { user_id: userId });
      setEmailHint(r.data.email_hint);
      toast.success('Codigo reenviado!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro ao reenviar'); }
    finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-[#050a15] flex items-center justify-center p-4 relative overflow-hidden">
      <Toaster position="top-center" richColors />
      <ParticleBg />

      <style>{`
        @keyframes float-particle { 0%,100%{transform:translateY(0) translateX(0)} 25%{transform:translateY(-20px) translateX(10px)} 50%{transform:translateY(-10px) translateX(-10px)} 75%{transform:translateY(-30px) translateX(5px)} }
        @keyframes glitch-1 { 0%,100%{clip-path:inset(0 0 0 0)} 20%{clip-path:inset(20% 0 60% 0);transform:translate(2px,-2px)} 40%{clip-path:inset(50% 0 20% 0);transform:translate(-2px,2px)} }
        @keyframes glitch-2 { 0%,100%{clip-path:inset(0 0 0 0)} 30%{clip-path:inset(30% 0 40% 0);transform:translate(-2px,1px)} 60%{clip-path:inset(60% 0 10% 0);transform:translate(2px,-1px)} }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes slideInRight { from{opacity:0;transform:translateX(60px)} to{opacity:1;transform:translateX(0)} }
        @keyframes slideInLeft { from{opacity:0;transform:translateX(-60px)} to{opacity:1;transform:translateX(0)} }
        @keyframes scaleIn { from{opacity:0;transform:scale(0.9)} to{opacity:1;transform:scale(1)} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        .animate-blink { animation: blink 0.8s step-end infinite; }
        .animate-glitch-1 { animation: glitch-1 3s ease-in-out infinite; }
        .animate-glitch-2 { animation: glitch-2 3s ease-in-out infinite reverse; }
        .anim-slide-next { animation: slideInRight 0.4s cubic-bezier(.16,1,.3,1) forwards; }
        .anim-slide-prev { animation: slideInLeft 0.4s cubic-bezier(.16,1,.3,1) forwards; }
        .anim-scale-in { animation: scaleIn 0.5s cubic-bezier(.16,1,.3,1) forwards; }
        .anim-fade-up { animation: fadeUp 0.5s cubic-bezier(.16,1,.3,1) forwards; }
        .code-input:focus { box-shadow: 0 0 0 2px hsl(var(--primary) / 0.3), 0 0 30px hsl(var(--primary) / 0.1); }
      `}</style>

      <div className="w-full max-w-md relative z-10">
        {/* Header */}
        <div className="text-center mb-8 anim-fade-up">
          <h1 className="font-bold text-3xl text-white tracking-tight">
            <GlitchText text="NEXU" className="text-white" /><span className="text-primary">ZAP</span>
          </h1>
          <p className="text-primary/60 font-mono text-xs mt-2 tracking-[0.3em] uppercase">Gestao de Clientes</p>
          <div className="flex items-center justify-center gap-3 mt-3">
            <div className="flex items-center gap-1.5 text-xs text-white/30">
              <Shield className="w-3.5 h-3.5 text-primary/50" /><span>Seguro</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-primary/30" />
            <div className="flex items-center gap-1.5 text-xs text-white/30">
              <Sparkles className="w-3.5 h-3.5 text-primary/50" /><span>Rapido</span>
            </div>
          </div>
        </div>

        {/* STEP: Username */}
        {step === 'user' && (
          <div key="user" className={animDir === 'next' ? 'anim-slide-next' : 'anim-slide-prev'}>
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-3xl p-8 shadow-2xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
              </div>

              <h2 className="text-xl font-bold text-white mt-4 mb-1">
                <TypewriterText text="Quem e voce?" />
              </h2>
              <p className="text-white/30 text-sm mb-6">Digite seu usuario ou email de acesso</p>

              {inputReady && (
                <div className="space-y-4 anim-fade-up">
                  <Input
                    ref={inputRef}
                    data-testid="username-input"
                    placeholder="Usuario ou email"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') goToPassword(); }}
                    className="h-14 bg-white/[0.03] border-white/[0.08] rounded-2xl text-white text-lg pl-5 pr-5 focus:border-primary/40 focus:bg-white/[0.05] transition-all code-input"
                    autoFocus
                    autoComplete="username"
                  />
                  <Button onClick={goToPassword} disabled={!username.trim()} data-testid="continue-btn"
                    className="w-full h-12 rounded-2xl text-base font-bold bg-primary hover:bg-primary/90 disabled:opacity-30 transition-all group">
                    Continuar
                    <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              )}

              <p className="text-center text-white/15 text-xs mt-6 font-mono">pressione Enter para continuar</p>
            </div>
          </div>
        )}

        {/* STEP: Password */}
        {step === 'password' && (
          <div key="password" className={animDir === 'next' ? 'anim-slide-next' : 'anim-slide-prev'}>
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-3xl p-8 shadow-2xl">
              <div className="flex items-center gap-3 mb-2">
                <button onClick={goBackToUser} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors">
                  <ArrowLeft className="w-4 h-4 text-white/50" />
                </button>
                <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-primary" />
                </div>
              </div>

              {/* User badge */}
              <div className="flex items-center gap-2 mt-4 mb-4 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.06] w-fit">
                <div className="w-7 h-7 rounded-lg bg-primary/15 flex items-center justify-center">
                  <span className="text-primary font-bold text-xs uppercase">{username.charAt(0)}</span>
                </div>
                <span className="text-white/60 text-sm font-medium">{username}</span>
                {savedUser && (
                  <button onClick={clearSavedUser} className="text-white/20 hover:text-red-400 transition-colors ml-1" title="Remover">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <h2 className="text-xl font-bold text-white mb-1">
                <TypewriterText text="Sua senha" />
              </h2>
              <p className="text-white/30 text-sm mb-6">Digite sua senha para acessar</p>

              {inputReady && (
                <div className="space-y-4 anim-fade-up">
                  <div className="relative">
                    <Input
                      ref={inputRef}
                      data-testid="password-input"
                      type={showPw ? 'text' : 'password'}
                      placeholder="Digite sua senha"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
                      className="h-14 bg-white/[0.03] border-white/[0.08] rounded-2xl text-white text-lg pl-5 pr-12 focus:border-primary/40 focus:bg-white/[0.05] transition-all code-input"
                      autoFocus
                      autoComplete="current-password"
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-primary transition-colors">
                      {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  <Button onClick={handleLogin} disabled={loading || !password} data-testid="login-btn"
                    className="w-full h-12 rounded-2xl text-base font-bold bg-primary hover:bg-primary/90 disabled:opacity-30 transition-all group">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                      <>
                        <LogIn className="w-5 h-5 mr-2" />
                        Entrar
                      </>
                    )}
                  </Button>

                  <div className="text-center">
                    <Link to="/forgot-password" className="text-sm text-white/30 hover:text-primary transition-colors">
                      Esqueceu sua senha?
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP: 2FA Verification */}
        {step === 'verification' && (
          <div key="verification" className="anim-scale-in">
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-3xl p-8 shadow-2xl text-center">
              <div className="w-20 h-20 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mx-auto mb-6">
                <Shield className="w-10 h-10 text-cyan-400" />
              </div>

              <h2 className="text-xl font-bold text-white mb-2">Verificacao 2FA</h2>
              <p className="text-white/50 text-sm mb-6">
                Codigo enviado para <span className="text-primary font-mono">{emailHint}</span>
              </p>

              <Input
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                placeholder="000000"
                className="h-16 text-center text-3xl font-mono tracking-[0.6em] bg-white/[0.03] border-white/[0.08] rounded-2xl text-white mb-4 code-input"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter' && verificationCode.length === 6) handleVerifyCode(); }}
              />

              <Button onClick={handleVerifyCode} disabled={loading || verificationCode.length !== 6}
                className="w-full h-12 rounded-2xl text-base font-bold bg-primary hover:bg-primary/90 disabled:opacity-30 mb-4">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verificar'}
              </Button>

              <div className="flex items-center justify-center gap-4">
                <button onClick={resendCode} disabled={resending} className="text-sm text-white/30 hover:text-primary transition-colors">
                  {resending ? 'Reenviando...' : 'Reenviar codigo'}
                </button>
                <span className="text-white/10">|</span>
                <button onClick={() => { setStep('user'); setAnimDir('prev'); setPassword(''); setVerificationCode(''); }}
                  className="text-sm text-white/30 hover:text-white transition-colors">
                  Voltar ao login
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP: Set Email */}
        {step === 'set_email' && (
          <div key="set_email" className="anim-scale-in">
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-3xl p-8 shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <button onClick={() => { setStep('password'); setAnimDir('prev'); }}
                  className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors">
                  <ArrowLeft className="w-4 h-4 text-white/50" />
                </button>
              </div>

              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Cadastre seu Email</h2>
                <p className="text-white/40 text-sm">Para sua seguranca, precisamos de um email</p>
              </div>

              <div className="space-y-4">
                <Input
                  type="email"
                  placeholder="seu@email.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSetEmail(); }}
                  className="h-14 bg-white/[0.03] border-white/[0.08] rounded-2xl text-white text-lg pl-5 focus:border-primary/40 code-input"
                  autoFocus
                />
                <Button onClick={handleSetEmail} disabled={loading || !newEmail.includes('@')}
                  className="w-full h-12 rounded-2xl text-base font-bold bg-primary hover:bg-primary/90 disabled:opacity-30">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                    <><Mail className="w-5 h-5 mr-2" />Enviar Codigo</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* STEP: Verify New Email */}
        {step === 'verify_new_email' && (
          <div key="verify_new_email" className="anim-scale-in">
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-3xl p-8 shadow-2xl text-center">
              <div className="w-20 h-20 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-6">
                <KeyRound className="w-10 h-10 text-primary" />
              </div>

              <h2 className="text-xl font-bold text-white mb-2">Confirme seu Email</h2>
              <p className="text-white/50 text-sm mb-6">
                Codigo enviado para <span className="text-primary font-mono">{newEmail}</span>
              </p>

              <Input
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                placeholder="000000"
                className="h-16 text-center text-3xl font-mono tracking-[0.6em] bg-white/[0.03] border-white/[0.08] rounded-2xl text-white mb-4 code-input"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter' && verificationCode.length === 6) handleVerifyNewEmail(); }}
              />

              <Button onClick={handleVerifyNewEmail} disabled={loading || verificationCode.length !== 6}
                className="w-full h-12 rounded-2xl text-base font-bold bg-primary hover:bg-primary/90 disabled:opacity-30 mb-4">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verificar e Entrar'}
              </Button>

              <button onClick={() => { setStep('set_email'); setAnimDir('prev'); setVerificationCode(''); }}
                className="text-sm text-white/30 hover:text-primary transition-colors">
                Alterar email
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-white/15 mt-8 font-mono">
          NexuZap &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
